import { useEffect, useRef, useState } from 'react';

interface UseMapZoomOptions {
    mapPlanRef: React.RefObject<HTMLDivElement>;
    isMobile: boolean;
    activeRoom: unknown;
}

// Синхронизировано с $app-default-duration: 0.25s
const ZOOM_DURATION = 250;

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

export const useMapZoom = ({ mapPlanRef, isMobile, activeRoom }: UseMapZoomOptions) => {
    const [zoomLevel, setZoomLevel] = useState(1);
    const pendingCenterRef = useRef<{ centerX: number; centerY: number } | null>(null);
    const rafRef = useRef<number | null>(null);

    const handleZoom = (action: 'in' | 'out' | 'reset') => () => {
        const step = isMobile ? 0.5 : 0.1;

        if (mapPlanRef.current) {
            const { scrollLeft, scrollTop, clientWidth, clientHeight } = mapPlanRef.current;
            pendingCenterRef.current = {
                centerX: scrollLeft + clientWidth / 2,
                centerY: scrollTop + clientHeight / 2,
            };
        }

        switch (action) {
            case 'reset':
                setZoomLevel(1);
                break;
            case 'in':
                setZoomLevel((prev) => Math.min(prev + step, isMobile ? 4 : 2));
                break;
            case 'out':
                setZoomLevel((prev) => Math.max(prev - step, 1));
                break;
        }
    };

    useEffect(() => {
        const container = mapPlanRef.current;
        if (!container) return;

        const contentEl = container.querySelector<HTMLElement>('.map-section-wrapper-item');

        // Текущий визуальный размер читаем из DOM, чтобы корректно прерывать анимацию
        const currentWidthPct = contentEl ? parseFloat(contentEl.style.width) || 100 : 100;
        const targetWidthPct = zoomLevel * 100;

        // Если зум не менялся визуально — считаем oldZoom равным текущему
        const oldZoom = currentWidthPct / 100;

        let targetScrollLeft: number;
        let targetScrollTop: number;

        if (pendingCenterRef.current) {
            const { centerX, centerY } = pendingCenterRef.current;
            const ratio = zoomLevel / oldZoom;
            targetScrollLeft = centerX * ratio - container.clientWidth / 2;
            targetScrollTop = centerY * ratio - container.clientHeight / 2;
            pendingCenterRef.current = null;
        } else {
            // Сброс (например, при закрытии комнаты) — сохраняем текущий центр
            const ratio = zoomLevel / oldZoom;
            const centerX = container.scrollLeft + container.clientWidth / 2;
            const centerY = container.scrollTop + container.clientHeight / 2;
            targetScrollLeft = centerX * ratio - container.clientWidth / 2;
            targetScrollTop = centerY * ratio - container.clientHeight / 2;
        }

        const startScrollLeft = container.scrollLeft;
        const startScrollTop = container.scrollTop;

        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
        }

        const startTime = performance.now();

        const animate = (now: number) => {
            const progress = Math.min((now - startTime) / ZOOM_DURATION, 1);
            const eased = easeInOut(progress);

            container.scrollLeft = startScrollLeft + (targetScrollLeft - startScrollLeft) * eased;
            container.scrollTop = startScrollTop + (targetScrollTop - startScrollTop) * eased;

            if (contentEl) {
                const pct = `${currentWidthPct + (targetWidthPct - currentWidthPct) * eased}%`;
                contentEl.style.width = pct;
                contentEl.style.height = 'auto';
            }

            if (progress < 1) {
                rafRef.current = requestAnimationFrame(animate);
            } else {
                rafRef.current = null;
            }
        };

        rafRef.current = requestAnimationFrame(animate);

        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [zoomLevel, mapPlanRef]);

    useEffect(() => {
        if (!activeRoom) {
            setZoomLevel(1);
        }
    }, [activeRoom]);

    return { zoomLevel, setZoomLevel, handleZoom };
};
